// Update the LoginPage.js file
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import './LoginPage.css'; // Import the custom styles

const LoginPage = ({ onLoginSuccess, maintenanceStatus }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false); // Add loading state for better UX
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    console.log('Rendering LoginPage (isAuthenticated should be false here)');
    const savedUsername = localStorage.getItem('kk_saved_username');
    const savedPassword = localStorage.getItem('kk_saved_password');
    if (savedUsername && savedPassword) {
      setUsername(savedUsername);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username || !password) {
      setError('Please enter both username and password.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/login/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      // Log IP address for debugging
      if (data.client_ip) {
        console.log('Login successful - Client IP:', data.client_ip);
      }

      if (response.ok) {
        // Handle "Remember Credentials" behavior
        if (rememberMe) {
          localStorage.setItem('kk_saved_username', username);
          localStorage.setItem('kk_saved_password', password);
        } else {
          localStorage.removeItem('kk_saved_username');
          localStorage.removeItem('kk_saved_password');
        }

        // --- FIX: Pass the entire data object to the parent component ---
        // The parent (App.js) will handle localStorage, state, and navigation.
        onLoginSuccess(data);
      } else {
        // Handle incorrect credentials and other API errors
        const errorMessage = data.detail || Object.values(data).join(' ');
        setError(errorMessage);
      }
    } catch (err) {
      console.error("Login API error:", err);
      setError('Network error or server unavailable.');
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-page-wrapper">
      <form className="login-container" onSubmit={handleSubmit} autoComplete="off">
        <h2>K & K Company Ltd.</h2>
        {maintenanceStatus?.locked && (
          <div className="error" style={{ background: '#fff3cd', color: '#856404', borderColor: '#ffeeba' }}>
            Maintenance mode is active. Login is disabled for all users except SuperAdmin.
          </div>
        )}
        {error && (
          <div className="error">
            {error}
          </div>
        )}
        
        <label htmlFor="username">Username</label>
        <input
          type="text"
          id="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          autoFocus
          style={{ textTransform: 'none' }} // Explicitly prevent uppercase
        />
        
        <label htmlFor="password">Password</label>
        <div className="password-field">
          <input
            type={showPassword ? "text" : "password"}
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="Password"
            style={{ textTransform: 'none' }} // Explicitly prevent uppercase
          />
          <span 
            className="eye-icon" 
            onClick={togglePasswordVisibility}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              // Eye with slash (hide password)
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            ) : (
              // Eye (show password)
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            )}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
          <input
            type="checkbox"
            id="rememberMe"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            style={{ width: 'auto', marginRight: '8px', marginBottom: '0', cursor: 'pointer' }}
          />
          <label htmlFor="rememberMe" style={{ marginBottom: '0', fontWeight: 'normal', fontSize: '0.9rem', cursor: 'pointer' }}>
            Remember Credentials
          </label>
        </div>
        
        <button type="submit" disabled={isLoading}>
          {isLoading ? 'Logging in...' : 'Login'}
        </button>
      </form>
    </div>
  );
};

export default LoginPage;